import React, { useMemo } from 'react';
import './EmailPreviewContent.scss';
import DOMPurify from 'dompurify';
import type { TEmailPreviewData } from 'components/FlowBuilder/FlowSidePanels/types';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ErrorIcon from 'assets/icons/icon-error-triangle.svg';

function decodeHtml(html: string): string {
  try {
    const jsonParsed = JSON.parse(html);
    if (typeof jsonParsed === 'string') {
      return jsonParsed;
    }
  } catch {
    // If JSON.parse fails, it's not a valid JSON string.
    // This is expected if the html is just a plain HTML string.
    // We can proceed with the original html string.
  }

  // Fallback to returning the original string if it's not a JSON-encoded string.
  return html;
}

function replaceTagsWithValues(html: string, data: Record<string, string> = {}) {
  // First, find all placeholders of the form {{ something }} (non-greedy inside)
  // Then replace if mapping exists, otherwise wrap with error span.
  // We intentionally do not allow nested braces inside placeholder name.
  const PLACEHOLDER_REGEX = /{{\s*([^{}\n\r]+?)\s*}}/g;

  return html.replace(PLACEHOLDER_REGEX, (_, inner) => {
    const key = inner.trim();

    if (key in data) {
      if (data[key] === null || data[key] === undefined || data[key] === '') {
        const errorIconImg = `<img src="${ErrorIcon}" class="error__icon" alt="Placeholder error" />`;
        return `<span title="Placeholder mapping needs to be corrected" class="error"><span class="error__text">{{${key}}}</span>${errorIconImg}</span>`;
      }
      return data[key];
    }

    return `{{${key}}}`;
  });
}

const EmailPreviewContent: React.FC<TEmailPreviewData> = ({ html, data, isLoading }) => {
  const sanitizedHtml = useMemo(() => {
    if (isLoading) return '';
    const decodedHtml = decodeHtml(html);
    const processedHtml = replaceTagsWithValues(decodedHtml, data);
    return DOMPurify.sanitize(processedHtml, {
      ADD_TAGS: ['style'],
      WHOLE_DOCUMENT: true,
    });
  }, [html, data, isLoading]);

  if (isLoading) {
    return (
      <div className="email-preview-content__skeleton">
        {/* Header block: single icon + subject + preheader stacked */}
        <div className="email-preview-content__skeleton__header">
          <div className="email-preview-content__skeleton__header__icon">
            <Skeleton height={60} width={60} />
          </div>
          <div className="email-preview-content__skeleton__header__text">
            <div className="email-preview-content__skeleton__header__text__subject">
              <Skeleton height={26} width="55%" />
            </div>
            <div className="email-preview-content__skeleton__header__text__preheader">
              <Skeleton height={14} width="35%" />
            </div>
          </div>
        </div>
        {/* Banner */}
        <div className="email-preview-content__skeleton__banner">
          <Skeleton height={150} />
        </div>
        {/* Body paragraphs */}
        <div className="email-preview-content__skeleton__paragraphs">
          <Skeleton height={14} width="90%" />
          <Skeleton height={14} width="95%" />
          <Skeleton height={14} width="88%" />
          <Skeleton height={14} width="60%" />
        </div>
        {/* CTA Button */}
        <div className="email-preview-content__skeleton__cta">
          <Skeleton height={40} width={180} />
        </div>
        {/* Footer lines */}
        <div className="email-preview-content__skeleton__footer">
          <Skeleton height={12} width="50%" />
          <Skeleton height={12} width="30%" />
        </div>
      </div>
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

export default EmailPreviewContent;
